import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, X, CheckCircle, XCircle, Settings, LogOut, Loader2, RefreshCw, Search, Filter, Download, TrendingUp, BarChart3, ShoppingBag, BookOpen, Users, Zap, BarChart, Image, Type, Lock } from 'lucide-react';
import { useToast } from './Toast';
import { formatDisplayDate, isToday } from '../utils/dateHelpers';
import { TableSkeleton } from './LoadingSkeleton';
import { AdminAvailabilityTab } from './AdminAvailabilityTab';
import { AdminStorefrontTab } from './AdminStorefrontTab';
import { AdminProductsTab } from './AdminProductsTab';
import { AdminOrdersTab } from './AdminOrdersTab';
import { AdminAnalyticsTab } from './AdminAnalyticsTab';
import { AdminEmailTab } from './AdminEmailTab';
import { AdminCoursesTab } from './AdminCoursesTab';
import { AdminFunnelsTab } from './AdminFunnelsTab';
import { AdminSettingsTab } from './AdminSettingsTab';
import { AdminLandingPageTab } from './AdminLandingPageTab';
import { AdminSubscribersTab } from './AdminSubscribersTab';
import { HelpTooltip, FeatureGuide, InfoBanner } from './HelpTooltip';

// Import custom hooks
import { useAuth } from '../hooks/useAuth';
import { useBookings } from '../hooks/useBookings';

// Tab definitions - extracted outside component
const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3, shortLabel: 'Overview' },
    { id: 'products', label: 'Products', icon: ShoppingBag, shortLabel: 'Products' },
    { id: 'landing', label: 'Landing Page', icon: Type, shortLabel: 'Landing' },
    { id: 'storefront', label: 'Storefront', icon: Image, shortLabel: 'Store' },
    { id: 'orders', label: 'Orders', icon: TrendingUp, shortLabel: 'Orders' },
    { id: 'bookings', label: 'Bookings', icon: Calendar, shortLabel: 'Bookings' },
    { id: 'subscribers', label: 'Subscribers', icon: Users, shortLabel: 'Subs' },
    { id: 'availability', label: 'Availability', icon: Clock, shortLabel: 'Times' },
    { id: 'funnels', label: 'Funnels', icon: Zap, shortLabel: 'Funnels' },
    { id: 'courses', label: 'Courses', icon: BookOpen, shortLabel: 'Courses' },
    { id: 'email', label: 'Email', icon: Mail, shortLabel: 'Email' },
    { id: 'analytics', label: 'Analytics', icon: BarChart, shortLabel: 'Stats' },
    { id: 'settings', label: 'Settings', icon: Settings, shortLabel: 'Settings' }
];

// Overview Tab Component - extracted outside AdminDashboard
function OverviewTab({ bookings, products, orders, stats }) {
    return (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800">Dashboard Overview</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-blue-600 font-medium">Total Bookings</p>
                            <p className="text-3xl font-bold text-blue-800 mt-1">{stats?.total || 0}</p>
                        </div>
                        <Calendar className="text-blue-500" size={32} />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-green-600 font-medium">Confirmed</p>
                            <p className="text-3xl font-bold text-green-800 mt-1">{stats?.confirmed || 0}</p>
                        </div>
                        <CheckCircle className="text-green-500" size={32} />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-purple-600 font-medium">Products</p>
                            <p className="text-3xl font-bold text-purple-800 mt-1">{products?.length || 0}</p>
                        </div>
                        <ShoppingBag className="text-purple-500" size={32} />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-orange-600 font-medium">Orders</p>
                            <p className="text-3xl font-bold text-orange-800 mt-1">{orders?.length || 0}</p>
                        </div>
                        <TrendingUp className="text-orange-500" size={32} />
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">Quick Stats</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                    <div>
                        <p className="text-2xl font-bold text-yellow-600">{stats?.pending || 0}</p>
                        <p className="text-sm text-gray-500">Pending</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-red-600">{stats?.cancelled || 0}</p>
                        <p className="text-sm text-gray-500">Cancelled</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-blue-600">{stats?.today || 0}</p>
                        <p className="text-sm text-gray-500">Today</p>
                    </div>
                    <div>
                        <p className="text-2xl font-bold text-green-600">{bookings?.length || 0}</p>
                        <p className="text-sm text-gray-500">All Time</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Bookings Tab Component - extracted for cleaner code
function BookingsTab({
    bookings,
    filteredBookings,
    loading,
    stats,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    loadBookings,
    handleStatusChange,
    handleDeleteBooking,
    exportBookings,
    getStatusColor
}) {
    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <h2 className="text-xl font-semibold text-gray-800">Bookings Management</h2>
                <HelpTooltip
                    title="Bookings Management"
                    content="View and manage all client appointment bookings. See booking details, confirm or cancel appointments, search and filter bookings, and export data. Bookings are created when clients book through your website. Auto-refreshes every 30 seconds to show new bookings."
                />
            </div>

            <FeatureGuide
                title="Bookings Management Guide"
                description="Manage all client appointment bookings in one place. View booking details, update status, search and filter, and export data for record-keeping. Bookings are automatically created when clients book through your website."
                steps={[
                    "View booking statistics at the top (Total, Pending, Confirmed, Cancelled, Today)",
                    "Use search to find bookings by name, email, date, time, or notes",
                    "Filter by status: All, Pending, Confirmed, or Cancelled",
                    "Click 'Confirm' to approve a booking (changes status to confirmed)",
                    "Click 'Cancel' to cancel a booking (changes status to cancelled)",
                    "Click 'Delete' to permanently remove a booking",
                    "Click 'Export CSV' to download all bookings as a spreadsheet",
                    "Click 'Refresh' to manually update the list (auto-refreshes every 30 seconds)"
                ]}
                tips={[
                    "Confirm bookings promptly to secure the appointment",
                    "Cancel bookings if clients need to reschedule",
                    "Use search to quickly find specific bookings",
                    "Export CSV regularly for backup and record-keeping",
                    "Pending bookings need your confirmation",
                    "Confirmed bookings are locked in and ready",
                    "Check 'Today' count to see today's appointments"
                ]}
            />

            <InfoBanner type="info" title="Booking Statuses">
                <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>Pending:</strong> New booking awaiting your confirmation. Review and confirm or cancel.</li>
                    <li><strong>Confirmed:</strong> Booking is approved and scheduled. Client is expecting this appointment.</li>
                    <li><strong>Cancelled:</strong> Booking was cancelled. Time slot becomes available again.</li>
                    <li><strong>Auto-Refresh:</strong> Bookings list updates automatically every 30 seconds to show new bookings.</li>
                </ul>
            </InfoBanner>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Total</p>
                            <p className="text-2xl font-bold text-blue-800 mt-1">{stats.total}</p>
                        </div>
                        <BarChart3 className="text-blue-500" size={24} />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-xl border border-yellow-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-yellow-600 font-medium uppercase tracking-wide">Pending</p>
                            <p className="text-2xl font-bold text-yellow-800 mt-1">{stats.pending}</p>
                        </div>
                        <Clock className="text-yellow-500" size={24} />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-green-600 font-medium uppercase tracking-wide">Confirmed</p>
                            <p className="text-2xl font-bold text-green-800 mt-1">{stats.confirmed}</p>
                        </div>
                        <CheckCircle className="text-green-500" size={24} />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-red-600 font-medium uppercase tracking-wide">Cancelled</p>
                            <p className="text-2xl font-bold text-red-800 mt-1">{stats.cancelled}</p>
                        </div>
                        <XCircle className="text-red-500" size={24} />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl border border-purple-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs text-purple-600 font-medium uppercase tracking-wide">Today</p>
                            <p className="text-2xl font-bold text-purple-800 mt-1">{stats.today}</p>
                        </div>
                        <TrendingUp className="text-purple-500" size={24} />
                    </div>
                </div>
            </div>

            {/* Actions Row */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-semibold text-gray-800">All Bookings ({filteredBookings.length})</h2>
                <div className="flex gap-2">
                    <button
                        onClick={exportBookings}
                        disabled={filteredBookings.length === 0}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center hover-lift button-press"
                    >
                        <Download size={16} className="mr-2" />
                        Export CSV
                    </button>
                    <button
                        onClick={loadBookings}
                        disabled={loading}
                        className="px-4 py-2 bg-gradient-to-r from-[#c5a059] to-[#b08e4d] text-white rounded-lg hover:shadow-md disabled:opacity-50 transition-all duration-200 flex items-center hover-lift button-press"
                    >
                        <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Search and Filter */}
            <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by name, email, date, time, or notes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] transition-all duration-200"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter size={18} className="text-gray-400" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="flex-1 px-4 py-2.5 rounded-lg border-2 border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] transition-all duration-200"
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Bookings List */}
            {loading ? (
                <TableSkeleton rows={5} />
            ) : filteredBookings.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                        {searchTerm || statusFilter !== 'all'
                            ? 'No bookings match your filters'
                            : 'No bookings yet'}
                    </p>
                    {(searchTerm || statusFilter !== 'all') && (
                        <button
                            onClick={() => {
                                setSearchTerm('');
                                setStatusFilter('all');
                            }}
                            className="mt-4 text-[#c5a059] hover:underline text-sm"
                        >
                            Clear filters
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4">
                    {filteredBookings.map((booking) => {
                        const bookingDate = new Date(booking.date + 'T00:00:00');
                        const isBookingToday = isToday(bookingDate);

                        return (
                            <div
                                key={booking.id}
                                className={`bg-gradient-to-br from-white to-[#fdfbf7] rounded-2xl border ${isBookingToday
                                        ? 'border-[#c5a059] shadow-md'
                                        : 'border-[#eaddcf]'
                                    } p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:border-[#c5a059]/30`}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    {/* Left Section - Booking Info */}
                                    <div className="flex-1 space-y-3">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#fae1e5] to-[#f3e6d5] flex items-center justify-center flex-shrink-0">
                                                <Calendar size={20} className="text-[#c5a059]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-lg font-serif text-gray-800">
                                                        {booking.name}
                                                    </h3>
                                                    {isBookingToday && (
                                                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                                                            Today
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="space-y-1.5">
                                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                                        <Calendar size={14} className="text-[#c5a059]" />
                                                        <span>{formatDisplayDate(bookingDate, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                                        <Clock size={14} className="text-[#c5a059]" />
                                                        <span>{booking.time}</span>
                                                    </div>
                                                    <a
                                                        href={`mailto:${booking.email}`}
                                                        className="text-sm text-gray-600 hover:text-[#c5a059] transition-colors flex items-center gap-2 group"
                                                    >
                                                        <Mail size={14} className="text-[#d48c95] group-hover:text-[#c5a059] transition-colors" />
                                                        <span>{booking.email}</span>
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Section - Status & Actions */}
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                                        <span className={`px-3 py-1.5 text-xs font-medium rounded-full ${getStatusColor(booking.status)}`}>
                                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            {booking.status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => handleStatusChange(booking.id, 'confirmed')}
                                                        className="p-2.5 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-xl transition-all duration-200"
                                                        title="Confirm Booking"
                                                        aria-label="Confirm booking"
                                                    >
                                                        <CheckCircle size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleStatusChange(booking.id, 'cancelled')}
                                                        className="p-2.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all duration-200"
                                                        title="Cancel Booking"
                                                        aria-label="Cancel booking"
                                                    >
                                                        <XCircle size={18} />
                                                    </button>
                                                </>
                                            )}
                                            {booking.status === 'confirmed' && (
                                                <button
                                                    onClick={() => handleStatusChange(booking.id, 'cancelled')}
                                                    className="p-2.5 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-xl transition-all duration-200"
                                                    title="Cancel Booking"
                                                    aria-label="Cancel booking"
                                                >
                                                    <XCircle size={18} />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDeleteBooking(booking.id)}
                                                className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
                                                title="Delete Booking"
                                                aria-label="Delete booking"
                                            >
                                                <X size={18} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function AdminDashboard({ onLogout }) {
    const [activeTab, setActiveTab] = useState('overview');
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [loading, setLoading] = useState(false);

    const { showToast } = useToast();
    const defaultTimeSlots = ["10:00 AM", "11:30 AM", "02:00 PM", "04:30 PM", "06:00 PM"];

    // Use custom hooks for auth and bookings
    const { isAuthenticated, user, authLoading, handleLogin, handleLogout } = useAuth();
    const bookingsData = useBookings({ isAuthenticated, activeTab });

    // Loading state
    if (authLoading) {
        return (
            <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-[#c5a059] animate-spin" />
            </div>
        );
    }

    // Not authenticated - show login screen
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-[#fdfbf7] flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl text-center">
                    <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Lock size={32} className="text-[#c5a059]" />
                    </div>
                    <h2 className="text-2xl font-serif text-gray-800 mb-2">Admin Access</h2>
                    <p className="text-gray-500 mb-8">Please sign in with your authorized Google account to access the dashboard.</p>

                    <button
                        onClick={handleLogin}
                        className="w-full py-3 px-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all flex items-center justify-center gap-3 group relative overflow-hidden"
                    >
                        <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                        <span>Sign in with Google</span>
                    </button>

                    <p className="mt-6 text-xs text-gray-400">
                        Secure access restricted to authorized administrators only.
                    </p>
                </div>
            </div>
        );
    }

    // Authenticated - show dashboard
    return (
        <div className="min-h-screen bg-[#fdfbf7]">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
                    <h1 className="text-xl sm:text-2xl font-serif text-[#c5a059]">Admin Dashboard</h1>
                    <button
                        onClick={() => handleLogout(onLogout)}
                        className="flex items-center text-gray-600 hover:text-[#d48c95] transition min-h-[44px] min-w-[44px] px-2 sm:px-0"
                        aria-label="Logout"
                    >
                        <LogOut size={18} className="sm:mr-2" />
                        <span className="hidden sm:inline">Logout</span>
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 border-t border-gray-200">
                    {/* Mobile: Dropdown Select */}
                    <div className="md:hidden py-3">
                        <select
                            value={activeTab}
                            onChange={(e) => setActiveTab(e.target.value)}
                            className="w-full px-4 py-2.5 bg-white border-2 border-gray-200 rounded-lg text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#c5a059] focus:border-[#c5a059] appearance-none"
                            style={{
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 0.75rem center',
                                paddingRight: '2.5rem'
                            }}
                        >
                            {TABS.map(({ id, label }) => (
                                <option key={id} value={id}>{label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Desktop: Tab Navigation */}
                    <div className="hidden md:flex space-x-1 py-2">
                        {TABS.map(({ id, label, icon: Icon, shortLabel }) => (
                            <button
                                key={id}
                                onClick={() => setActiveTab(id)}
                                className={`py-3 px-4 border-b-2 transition flex items-center gap-2 whitespace-nowrap min-h-[48px] ${activeTab === id
                                        ? 'border-[#c5a059] text-[#c5a059] font-medium'
                                        : 'border-transparent text-gray-600 hover:text-gray-800'
                                    }`}
                            >
                                <Icon size={20} />
                                <span className="text-sm lg:hidden">{shortLabel}</span>
                                <span className="hidden lg:inline text-sm">{label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
                {activeTab === 'overview' && (
                    <OverviewTab
                        bookings={bookingsData.bookings}
                        products={products}
                        orders={orders}
                        stats={bookingsData.stats}
                    />
                )}

                {activeTab === 'products' && (
                    <AdminProductsTab
                        products={products}
                        setProducts={setProducts}
                        showToast={showToast}
                        loading={loading}
                        setLoading={setLoading}
                    />
                )}

                {activeTab === 'landing' && (
                    <AdminLandingPageTab
                        showToast={showToast}
                    />
                )}

                {activeTab === 'storefront' && (
                    <AdminStorefrontTab
                        showToast={showToast}
                    />
                )}

                {activeTab === 'orders' && (
                    <AdminOrdersTab
                        orders={orders}
                        setOrders={setOrders}
                        showToast={showToast}
                        loading={loading}
                        setLoading={setLoading}
                    />
                )}

                {activeTab === 'analytics' && (
                    <AdminAnalyticsTab
                        bookings={bookingsData.bookings}
                        products={products}
                        orders={orders}
                    />
                )}

                {activeTab === 'courses' && (
                    <AdminCoursesTab
                        showToast={showToast}
                    />
                )}

                {activeTab === 'funnels' && (
                    <AdminFunnelsTab
                        showToast={showToast}
                    />
                )}

                {activeTab === 'email' && (
                    <AdminEmailTab
                        showToast={showToast}
                    />
                )}

                {activeTab === 'subscribers' && (
                    <AdminSubscribersTab showToast={showToast} />
                )}

                {activeTab === 'bookings' && (
                    <BookingsTab {...bookingsData} />
                )}

                {activeTab === 'availability' && (
                    <AdminAvailabilityTab
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        defaultTimeSlots={defaultTimeSlots}
                        showToast={showToast}
                    />
                )}

                {activeTab === 'settings' && (
                    <AdminSettingsTab />
                )}
            </div>
        </div>
    );
}
